export type JoinId = string;

export interface JoinedFieldMapping {
  readonly sourceField: string;
  /** Stable renderer-independent property key used by authored layer configuration. */
  readonly outputFieldId: string;
  readonly displayLabel: string;
}

export interface JoinConfig {
  readonly id: JoinId;
  readonly type: 'left';
  readonly tableSourceId: string;
  /** Native vector-source field only. */
  readonly leftField: string;
  /** Native table-source field only. */
  readonly rightField: string;
  readonly name: string;
  readonly fields: readonly JoinedFieldMapping[];
}

export type JoinDraft = Pick<JoinConfig, 'leftField' | 'rightField' | 'tableSourceId'> & {
  readonly fields: readonly string[];
};

export type JoinIdFactory = (prefix: 'join' | 'jfld') => string;

const createId: JoinIdFactory = (prefix) => `${prefix}_${crypto.randomUUID()}`;

export function createJoinConfig(
  draft: JoinDraft,
  tableName: string,
  createJoinId: JoinIdFactory = createId,
): JoinConfig {
  return {
    id: createJoinId('join'),
    type: 'left',
    tableSourceId: draft.tableSourceId,
    leftField: draft.leftField,
    rightField: draft.rightField,
    name: tableName,
    fields: draft.fields.map((sourceField) => ({
      sourceField,
      outputFieldId: createJoinId('jfld'),
      displayLabel: `${tableName} · ${sourceField}`,
    })),
  };
}

export function isExactJoinRelationship(
  join: JoinConfig,
  draft: Pick<JoinDraft, 'leftField' | 'rightField' | 'tableSourceId'>,
): boolean {
  return (
    join.tableSourceId === draft.tableSourceId &&
    join.leftField === draft.leftField &&
    join.rightField === draft.rightField
  );
}

/**
 * Replaces one authored relationship while retaining its stable identity. Field
 * identities survive when the same source attribute remains selected; newly
 * selected attributes receive new identities.
 */
export function updateJoinConfig(
  current: JoinConfig,
  draft: JoinDraft,
  tableName: string,
  createJoinId: JoinIdFactory = createId,
): JoinConfig {
  const currentFields = new Map(current.fields.map((field) => [field.sourceField, field]));
  return {
    ...current,
    tableSourceId: draft.tableSourceId,
    leftField: draft.leftField,
    rightField: draft.rightField,
    name: tableName,
    fields: draft.fields.map((sourceField) => {
      const existing = currentFields.get(sourceField);
      return {
        sourceField,
        outputFieldId: existing?.outputFieldId ?? createJoinId('jfld'),
        displayLabel: `${tableName} · ${sourceField}`,
      };
    }),
  };
}
